import {
	Box,
	Button,
	debounce,
	Dialog,
	Grid,
	InputAdornment,
	Snackbar,
	Stack,
	Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { DataPipelineModal } from './modal-component';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import '../DataPipeline/dataPipeline.scss';
import {
	exportProjectListByProgram,
	getProgramById,
	getProgramSectorList,
	getProjectListByProgram
} from '../../service/service';
import { CustomizedDataGrid } from '../customized_data_grid';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import DataInfoSecondary from 'components/Widgets/DataInfoSecondary';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import ShowInfoWidget from 'components/Widgets/ShowInfoWidget';
import RowTypography from 'components/Table/RowTypography';
import eventEmitter from 'SnackBarEventEmitter';
import DataInfoColorWidget from 'components/Widgets/DataInfoColorWidget';
import SingleSelectFilter from 'components/Dropdown/SingleSelectFilter';
import CustomTextField from 'components/Inputs/CustomTextField';
import { Search } from '@mui/icons-material';
import { checkRole } from 'utils/roleUtils';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from "@mui/material/styles";
import { getPaginationSlice, getProjectCommonColumns, getProjectExportConfig, setRdgTableHeight } from './DataPipelineHelper';
import { projectDetailLinkFormatter } from './DataPipelineFormatter';
import DataInfoSummaryCard from 'components/Widgets/DataInfoSummaryCard';
import { useHeader } from "Context/contexts/HeaderTabRightContentContext";
import DateTimeRangeFilter from 'components/Dropdown/DateTimeRangeFilter/DateTimeRangeFilter';
import AddIcon from '@mui/icons-material/Add';

interface ProjectQueryParams {
    filterInfo?: any, 
	currentFilterType?: string, 
	page?: number, 
	pageSize?: number, 
	stDate?: string, 
	edDate?: string, 
	sortingInfo?: any, 
	sectorId?: string, 
	projName?: string
}

const PROGRAM_DATE_FIXED_OPTIONS = [
	{ val: 'today', label: 'Today' },
	{ val: 'yesterday', label: 'Yesterday' },
	{ val: '7d', label: 'Last 7 Days' },
	{ val: '30d', label: 'Last 30 Days' },
	{ val: '90d', label: 'Last 3 Months' }
];

const ProgramDetails = () => {
	const theme = useTheme();
	const { setRightContextContent } = useHeader();
	const [controller] = usePepwiseAuthController();
	const { role } = controller;
	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User','Pwc_Edit']);
	
	const [currentFilterType, setCurrentFilterType] = useState<string>('');
	const [hoveredItem, setHoveredItem] = useState<string | null>(null);
	const [clickedItem, setClickedItem] = useState<string | null>(null);
	const [sectorMasterList, setSectorMasterList] = useState<any[]>([]);
	const [sectorList, setSectorList] = useState<any[]>([]);
	const [sectorName, setSectorName] = useState('all');
	const hiddenSector = "SODASTREAM";
	const filteredSectorList = sectorList.filter(sector => sector !== hiddenSector);

	const handleThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const { id, name } = useParams();

	const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {

		event.preventDefault();
		event.stopPropagation();
	};
	const handleDetailsThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const [startDate, setStartDate] = useState(moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD'));
	const [endDate, setEndDate] = useState(moment().tz("America/Chicago").endOf('day').format('YYYY-MM-DD'));
	const [dateRangeType, setDateRangeType] = useState<string>('today');
	const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
	const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
	const [projectList, setProjectList] = useState<any>([]);
	const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });
	const [programDetail, setProgramDetail] = useState<any>({});
	const [loading, setLoading] = useState(false);
	const [tableLoading, setTableLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0, currentPage: 0 });
	const [projectsDataList, setProjectsDataList] = useState<any[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const defaultColumnNames = ['Sectors', 'Project', 'Failed', 'Success', 'SLA Status', 'SLA Time (CST)', 'Actual Completion Time (CST)', 'Action'];
	const [filterData, setFilterData] = useState<any[]>([]);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const searchDetails: any[] = [];
	const [filterInfo, setFilterInfo] = useState<any>({});
	const [searchProject, setSearchProject] = useState('');
	const [toast, setToast] = useState<any>({
		open: false,
		message: ''
	});

	const navigate = useNavigate();
	const programIdParam = programDetail.Program_Id;
	const [selectedDateFilter, setSelectedDateFilter] = useState<string>('Today');

	const getColumns = () => {
		return [
			{
				key: 'Sectors',
				name: 'Sectors',
				formatter(props: any) {
					const sectorList = props.row.Sectors ? props.row.Sectors.split(', ') : [];
					if (sectorList?.length) {
						return sectorList.map((sector: string, idx: number) => <RowTypography sx={{ backgroundColor: theme.palette.table.chipBackground, color: theme.palette.table.chipText, padding: '4px 10px', marginRight: '5px', borderRadius: '20px' }} key={sector} title={sector}>{sector}</RowTypography>);
					} else {
						return '--'
					}
				},
				minWidth: 150,
				maxWidth: 450
			},
			{
				key: 'Project_Name',
				name: 'Project',
				formatter: projectDetailLinkFormatter(
					programDetail?.program_Name,
					startDate,
					endDate,
  					selectedDateFilter
				),
				minWidth: 200,
				maxWidth: 500
			},
			...getProjectCommonColumns({ isAllowed, handleAction }),
		];
	};

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key] && filterInfo[key].length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};

	const handleFilter = (filterInfo: any) => {

		setFilterInfo(filterInfo);
		setClickedItem(null);

		// if clear all is triggered -> reset widget filter
		const isClearAll = filterInfo && Object.values(filterInfo).every(
			(v: any) => Array.isArray(v) && v.length === 0
		);
		if (isClearAll) {
			setCurrentFilterType('');  
		}

		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType: '', page, pageSize, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
	};

	const handleSearch = (e: any) => {
		console.log('FILTER', e);
	};

	const handleSorting = (sortingInfo: any) => {

		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page, pageSize, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
		setSortingInfo(sortingInfo)
	};

	const onClickWidget = (type: string) => {
		const filterType = type && (type !== 'Total Projects' && type !== `${sectorName} Projects`) ? type : "";


		setCurrentFilterType(filterType);


		getProjectsByProgram({
			filterInfo: type && type != 'Total Projects' && type !== `${sectorName} Projects` ? {projectSlaStatus: [filterType]} : {},
			currentFilterType: filterType || undefined,
			page,
			pageSize,
			stDate: startDate,
			edDate: endDate,
			sortingInfo,
			sectorId: getSectorId()
		});
	};

	eventEmitter.on('showSnackbar', (message: string) => {
		setLoading(false);
		setTableLoading(false)
	});

	useEffect(() => {
		document.body.classList.add("v2Background");
		const activeId = document.getElementById('root');
		if (window.location.pathname.includes('/data-pipeline/programs')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}
		if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
			setSectorList(JSON.parse(localStorage.getItem('filterSectors') ?? ''));
			setSectorMasterList(JSON.parse(localStorage.getItem('sectorsMasterData') ?? ''));
		} else {
			getSectorData();
		}
		getProgramDetails(getSectorId(), startDate, endDate);
		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
	}, []);

	const handleSectorChange = (value: string) => {
		setSectorName(value);
		setSearchProject('');
		localStorage.setItem('selectedSector', value);
		let sectorId;
		if (value != 'all') {
			sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == value)[0].Sector_Id
		}
		setProjectList([]);
		getProgramDetails(sectorId, startDate, endDate);
		getProjectsByProgram({filterInfo: currentFilterType ? { projectSlaStatus: [currentFilterType] } : {}, currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId});
	};

	const onProjectSearch = (value: string) => {
		setSearchProject(value)
		debouncedFetchOptions(value)
	}

	const debouncedFetchOptions = useMemo(
		() =>
			debounce(async (value) => {

				getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo: { sortBy: '', sortDir: '' }, sectorId: getSectorId(), projName: searchProject || value});

			}, 500),
		[startDate, endDate]
	);




	useEffect(() => {
		if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType == 'updated') {
			let sectorId;
			if (sectorName && sectorName != 'all') {
				sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
			}
			setSearchProject('');
			getProgramDetails(sectorId, startDate, endDate);
			getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId});
		}
	}, [openModal]);

	const getSectorData = async () => {
		let sectors: any = await getProgramSectorList();
		const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
		localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
		localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
		setSectorList(filterSectors);
		setSectorMasterList(sectors.data.sectors);
	};

	const passInfoToParent = (data: any) => {
		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100});
	};

	const getProgramDetails = async (sectorId?: string, fromDate?: string, toDate?: string) => {
		setLoading(true);
		fromDate = normalizeDate(fromDate);
		toDate = normalizeDate(toDate);	
		let result = await getProgramById(id, sectorId, fromDate, toDate);
		setLoading(false);
		setProgramDetail(result.data?.programData ? result.data.programData[0] : '');
		if (!result.data.programData.length) {
			navigate(`/data-pipeline/dataOps`);
		}
	};

	const normalizeDate = (dateStr?: string) => {
		if (!dateStr) return dateStr;
		const localToday  = moment().format('YYYY-MM-DD');
		const chicagoToday = moment().tz('America/Chicago').format('YYYY-MM-DD');
		// Today filter case
		if (dateStr === localToday  && localToday  !== chicagoToday) {
			return moment(dateStr).tz('America/Chicago').format('YYYY-MM-DD');
		}
		return dateStr;
	};

	const getProjectsByProgram = async ({
        filterInfo,
        currentFilterType,
        page,
        pageSize,
        stDate,
        edDate,
        sortingInfo,
        sectorId,
        projName
    }: ProjectQueryParams = {}) => {
		setTableLoading(true);

		stDate = stDate || startDate
		edDate = edDate || endDate
		stDate = normalizeDate(stDate);
		edDate = normalizeDate(edDate);
		sectorId = sectorId || getSectorId()
		const fetchPageNumber = 1;
		const fetchPageSize = 10000;
		let result = await getProjectListByProgram(id, fetchPageNumber, fetchPageSize, currentFilterType, filterInfo, stDate, edDate, sortingInfo, sectorId, projName || '');
		setTimeout(() => {
			setRdgTableHeight(
				`project_list${id}`,
				result.data.projectList.length
			);
		}, 100);
		setTableLoading(false);
		const projData = result?.data?.projectList?.map((list: any) => {
			list['activeRow'] = 0;
			const selectedSectors = list.Sectors && list.Sectors.split(', ');
			list['Sectors'] = selectedSectors && selectedSectors.length ? list.Sectors : "";
			return list;
		});
		setProjectsDataList(projData);
		// Set pagination info (client side)
		const totalElements = projData.length;
  		const totalPages = Math.ceil(totalElements / Number(pageSize || 100));
		setPaginationData({
			totalElements,
			totalPages,
			currentPage: 1
		});
		setPage(1);
  		setProjectList(projData.slice(0, Number(pageSize)));
		const filtersData: any = {
			Sectors: [], Project_Name: [], SLA_Status: []
		}
		if (result.data.filterOptions) {
			Object.entries(result.data.filterOptions).forEach(([key, value]: any) => {
				if (key == 'sector' || key == 'Sector_Name') {
					filtersData['Sectors'] = value;
				}
				else if (key == 'Project_Name' || key == 'projectName') {
					filtersData['Project_Name'] = value;
				} else if (key == 'SLA_Status' || key == 'projectSlaStatus') {
					filtersData['SLA_Status'] = value.filter(Boolean);
				}
			})
		}
		setFilterData(filtersData);
		setFilterInfo(currentFilterType ? filtersData : filterInfo)
	};

	// export
	const exportGetProjectsByProgram = async (filterInfo?: any, currentFilterType?: string, stDate?: string, edDate?: string, sortingInfo?: any, sectorId?: string, projName?: string) => {
		stDate = stDate || startDate
		edDate = edDate || endDate
		
		const payload = getProjectExportConfig({
			filterInfo,
			currentFilterType,
			stDate,
    		edDate,
    		sortingInfo,
    		sectorId: sectorId || getSectorId(),
    		projName,
    		programID: id,
			programName: name
  		});

  		await exportProjectListByProgram(payload);

	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage);
		
		const data = getPaginationSlice({
			list: projectsDataList,
			newPage,
			pageSize
		});
		setProjectList(data);
	};

	const handlepageSizeChange = (event: any) => {
		const newPageSize = Number(event.target.value);
  		setPageSize(newPageSize);
  		setPage(1);
		const data = projectsDataList.slice(0, newPageSize);
		setProjectList(data);
    	setPaginationData({
			...paginationData,
    		totalPages: Math.ceil(projectsDataList.length / newPageSize),
   			totalElements: projectsDataList.length,
    		currentPage: 1
		});
	};

	const handleEditClose = (closeType: any) => {
		setOpenModal({
			...openModal,
			isOpen: false,
			closeType: closeType
		});
	};

	const onCreateProgramClick = (event: any) => {
		setOpenModal({
			...openModal,
			isOpen: true,
			type: 'Project',
			action: 'CREATE'
		});
		navigate(`/data-pipeline/programs-tab?programID=${programIdParam}&programName=${programDetail.program_Name}&tab=1&isEdited=true`);
	};

	const handleAction = (module: string, type: string, data: any, e: any) => {

		e.preventDefault();
		e.stopPropagation();
		handleThreeDotsClose(e);
		handleDetailsThreeDotsClose(e);
		data = {
			...data,
			ProgramName: programDetail?.program_Name,
			ProgramID: data.Program_ID || data.Program_Id
		};

		switch (type) {
			case 'edit':
				setOpenModal({
					...openModal,
					isOpen: true,
					type: module,
					action: 'EDIT',
					data: data
				});
				if (module == 'Program') {
					navigate(`/data-pipeline/programs-tab?programID=${programIdParam}&programName=${programDetail.program_Name}&isEdited=true&tab=0`);
				} else if (module == 'Project') {
					navigate(`/data-pipeline/programs-tab?programID=${programIdParam}&programName=${programDetail.program_Name}&isEdited=true&tab=1&projectID=${data.Project_Id}`);
				}
				break;
			case 'delete':
				setOpenModal({
					...openModal,
					isOpen: true,
					type: module,
					action: 'DELETE',
					data: data
				});
				break;
			case 'view':
				window.location.href = `/data-pipeline/projects/${data.Program_ID}/${data.Project_Id}/${encodeURIComponent(
					data.ProgramName
				)}/${encodeURIComponent(data.Project_Name)}`;
				break;
			default:
				break;
		}
	};

	const handleClose = () => {
		setToast({
			...toast,
			open: false
		});
	};

	const getToastData = (data: any) => {
		setToast({
			...toast,
			message: data.data.message
		});
	};

	const getSectorId = (): string | undefined => {
		let sectorId;
		let selectedSctr = localStorage.getItem('selectedSector')
		if (selectedSctr && selectedSctr != 'all') {
			setSectorName(selectedSctr)
			const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '')
			if (sectorList) {
				sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
			}
		}
		return sectorId;
	}

	const handleDateRangeChange = (type: string, start?: Date, end?: Date) => {
		if (!start || !end) return;

		setSearchProject('');

		const sd = moment(start).tz('America/Chicago').format('YYYY-MM-DD');
		const ed = moment(end).tz('America/Chicago').format('YYYY-MM-DD');

		setDateRangeType(type);
		setStartDate(sd);
		setEndDate(ed);

		if (type === 'custom') {
			setCustomStartDate(start);
			setCustomEndDate(end);
		} else {
			setCustomStartDate(null);
			setCustomEndDate(null);
		}

		const selectedOption = PROGRAM_DATE_FIXED_OPTIONS.find(option => option.val === type);
		setSelectedDateFilter(selectedOption?.label ?? (type === 'custom' ? 'Custom' : type));

		const newFilterInfo = filterInfo?.projectSlaStatus ? { projectSlaStatus: filterInfo.projectSlaStatus } : {};
        setFilterInfo(newFilterInfo);

		if (moment(sd).isValid() && moment(ed).isValid()) {
			getProgramDetails(getSectorId(), sd, ed);
			getProjectsByProgram({filterInfo: newFilterInfo, currentFilterType, page: 1, pageSize: 100, stDate: sd, edDate: ed, sortingInfo, sectorId: getSectorId()});
		}
	}

	let emptyStateContent;
	if (tableLoading) {
		emptyStateContent = (
			<Stack justifyContent="center" alignItems="center" height="60vh">
				<Typography variant="pageTitle" color="dark.main">
					Loading...
				</Typography>
			</Stack>
		);
	} else {
		emptyStateContent = (
			<ShowInfoWidget
				title="No projects have been added yet"
				height="50vh"
				button="+ ADD New Project"
				onClick={onCreateProgramClick}
				isEditable={isAllowed}
			/>
		);
	}

	const isTotalProjects = (title: string) =>
  		title === 'Total Projects' || title.includes('Total Projects');

	const getWidgetClassName = (title: string): string => {
		const isActive = hoveredItem === title || clickedItem === title;

		if (isActive && !isTotalProjects(title)) {
			switch (title) {
				case 'Completed Projects':
					return 'solidBlue';
				case 'SLA Met':
					return 'pgmGreen';
				case 'SLA Missed':
					return 'pgmPink';
				case 'In Progress':
					return 'pgmYellow';
				case 'Scheduled':
				case 'Not Scheduled':
					return 'paleGray';
				default:
					return '';
			}
		}
		return title === 'Not Scheduled' ? 'NotScheduledColor' : 'none';
	};

	const handleMouseEnter = (title: string, value: any) => {
	if (value) {
		setHoveredItem(title);
	}
	};

	const handleMouseLeave = () => {
	setHoveredItem(null);
	};

	const handleWidgetClick = (title: string, value: any, isGrid: boolean = false) => {
		if (!value) return;

		setClickedItem(title);
		onClickWidget((isGrid || !isTotalProjects(title)) ? title : '');
	};

useEffect(() => {
	setRightContextContent(
		<Box display="flex" alignItems="center" gap={2}>
			<DateTimeRangeFilter
				dateRangeType={dateRangeType}
				customStartDate={customStartDate}
				customEndDate={customEndDate}
				onChange={handleDateRangeChange}
				timezone="CST"
				fixedOptions={PROGRAM_DATE_FIXED_OPTIONS}
			/>
			<SingleSelectFilter
				sectorName={sectorName}
				setSectorName={setSectorName}
				sectorOptionsList={filteredSectorList}
				handleChange={handleSectorChange}
			/>
		</Box>,
	);

	return () => {
		setRightContextContent(null);
	};
}, [sectorName, sectorList, dateRangeType, customStartDate, customEndDate, filterInfo, currentFilterType, sortingInfo, sectorMasterList]);

	return (
		<PageWrapper>
		<Box 
			className={"main-bordered-container"}
			sx={{
                backgroundColor: `${theme.palette.backgroundLight.primary} !important`,
                minHeight: '100vh'
            }}
			>
			<PageHeader
				icon={DashboardIcon}
				title={[
					{
						text: 'Programs',
						link: '/data-pipeline/dataOps'
					},
					{
						text: decodeURIComponent(window.location.pathname.split('/')[4].replace(/%20/g, ' '))
					}
				]}
				isPepwise2Breadcrumb
			>

				{isAllowed && (
					<Button
						variant="pepwiseSecondary"
						onClick={onCreateProgramClick}
						startIcon={
							<AddIcon
								sx={{
									fontSize: '20px',
									'& path': {
										strokeWidth: 2,
									},
								}}
							/>
						}
						sx={{ height: '43px' }}
					>
						ADD PROJECT
					</Button>
				)}
			</PageHeader>

			<Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
				<DataPipelineModal
					handleEditClose={handleEditClose}
					programData={openModal}
					getToastData={getToastData}
					passInfoToParent={passInfoToParent}
				/>
			</Dialog>
			<Box p={3} pr={2} mt={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} lg={5}>
						<DataInfoSummaryCard
							infoCards={[
								{
									label: 'Program Name',
									value: programDetail?.program_Name,
									isLoading: !programDetail?.program_Name,
									valueProps: {
										lineHeight: '24px'
									}
								}
							]}
							infoTooltip={{
								dateValues: [
									{ label: 'Date Created', value: programDetail?.Created_Dt ?? 'NA' },
									{ label: 'Last Updated', value: programDetail?.Updated_Dt ?? 'NA' }
								]
							}}
							infoOptionMenu={isAllowed ? {
								onClick: handleDetailsThreeDotsClick,
								menuItems: [
									{
										key: 'edit',
										label: 'Edit',
										icon: <EditIcon />,
										onMenuClick: (e: any) => handleAction('Program', 'edit', programDetail, e)
									},
									{
										key: 'delete',
										label: 'Delete',
										icon: <DeleteIcon />,
										onMenuClick: (e: any) => handleAction('Program', 'delete', programDetail, e)
									}
								]
							} : undefined}
							isEditEnabled={isAllowed}
						/>
					</Grid>


					<Grid item container xs={12} lg={7} spacing={1}>
						{[
							{
								title: sectorName != 'all' ? sectorName + ' Projects' : 'Total Projects',
								value: programDetail?.ProjectCount,
								isLoading: loading,
								color: ''
							},
							{
								title: 'Scheduled',
								value: programDetail?.Scheduled_Count || 0,
								isLoading: loading,
								color: 'paleGray'
							},
							{
								title: 'Completed Projects',
								value: (programDetail?.SLA_Met_Count || 0) + (programDetail?.SLA_Missed_Count || 0),
								isLoading: loading,
								color: 'solidBlue'
							},
							{
								title: 'SLA Met',
								value: programDetail?.SLA_Met_Count || 0,
								isLoading: loading,
								color: 'pgmGreen'
							},
							{
								title: 'SLA Missed',
								value: programDetail?.SLA_Missed_Count || 0,
								isLoading: loading,
								color: 'pgmPink'
							},
							{
								title: 'In Progress',
								value: programDetail?.In_Progress_Count || 0,
								isLoading: loading,
								color: 'pgmYellow'
							},
							{
								title: 'Not Scheduled',
								value: programDetail?.Not_Scheduled_Count || 0,
								isLoading: loading,
								color: 'pgmGray',
							},

						].map(({ title, value, color, isLoading }) => (
							<Grid
								key={title}
								item
								xs={3}
								sm={1.7}
								lg={1.7}
								display="flex"
								onMouseEnter={() => handleMouseEnter(title, value)}
								onMouseLeave={handleMouseLeave}
								onClick={() => handleWidgetClick(title, value, true)}
								className="DataInfo"
							>
								<DataInfoColorWidget
									title={title}
									className={getWidgetClassName(title)}
									style={{ cursor: value ? 'pointer' : 'default' }}
									onClick={() => handleWidgetClick(title, value)}
									value={value}
									boxColor={color}
									isLoading={isLoading}
								/>
							</Grid>
							))}
					</Grid>


				</Grid>
				<Box sx={{ position: 'relative' }}>
					<Typography mt={3} variant="overlayWidgetTitle" sx={{ position: 'absolute', color: `${theme.palette.table.title} !important` }} component="span"> Projects</Typography>
					{programDetail?.ProjectCount > 0 && <CustomTextField
						placeholder="Search Projects"
						value={searchProject}
						onChange={(e) => onProjectSearch(e.target.value)}
						style={{
							width: '22rem',
							position: 'absolute',
							top: '25px',
							right: '70px',
							borderRadius: '5px',
							zIndex: '99'
						}}
						sx={{
							backgroundColor: '#F2F6FD',
							borderRadius: '5px'
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search
										sx={{
											color: '#98A6CC',
											fontSize: '26px'
										}}
									/>
								</InputAdornment>
							),
							sx: {
								height: '38px',
								backgroundColor: '#fff',
								borderRadius: '5px',
								color: '#000000',
								fontFamily: 'Montserrat',
								fontSize: '14px',
								'& .MuiInputAdornment-root': {
									marginRight: '8px'
								},
								'& .MuiInputBase-input': {
									padding: '8px 12px 8px 0',
									color: '#000000',
									fontFamily: 'Montserrat',
									fontSize: '14px',
									fontWeight: 400,
									'&::placeholder': {
										color: '#98A6CC',
										opacity: 1
									}
								},
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: '#A5B1CD',
									borderWidth: '1px',
									borderStyle: 'solid'
								},
								'&:hover .MuiOutlinedInput-notchedOutline': {
									borderColor: '#A5B1CD',
									borderWidth: '1px'
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: '#A5B1CD',
									borderWidth: '1px'
								}
							}
						}}
					/>
					}
				</Box>
				{programDetail?.ProjectCount > 0 && !tableLoading ? (
					<Box sx={{ position: 'relative' }}>




						{projectList?.length > 0 && (
							<Box>
								<CustomizedDataGrid
									tableColumns={getColumns()}
									tableData={projectList}
									tableType="program-list"
									defaultColumnNames={defaultColumnNames}
									onSorting={handleSorting}
									currSorting={sortingInfo}
									filterDataOptions={filterData}
									searchDataValues={searchDetails}
									onFilterCheck={handleFilter}
									currFilter={filterInfo}
									onFilterSearch={handleSearch}
									page={page}
									pageSize={pageSize}
									paginationData={paginationData}
									listLength={projectsDataList.length}
									handlepageSizeChange={handlepageSizeChange}
									handlePageChange={handlePageChange}
									isPrevNextPagination
									isFetching={tableLoading}
									showExport={true}
									fileName={`programs_Page_${paginationData.currentPage}_of_${paginationData.totalPages}.xlsx`}
									exportTableData={() => {
										const parsed = parseFilter(filterInfo);
										const widgetFilter = currentFilterType;
										return exportGetProjectsByProgram(parsed, widgetFilter, startDate, endDate, sortingInfo, getSectorId(), ""); 
									}}
									isDateRangeFilter={false}
									isDisabled={false}
									isShowMoreButton
								/>
							</Box>
						)}
						{!projectList?.length && <Stack justifyContent="center" alignItems="center" height="60vh">
							<Typography variant="pageTitle" color="dark.main">
								No data for the selected filters
							</Typography>
						</Stack>}
					</Box>
				) : emptyStateContent}
			</Box>
			<Snackbar
				open={toast && openModal.message ? true : false}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				autoHideDuration={6000}
				onClick={handleClose}
				message={openModal.message}
			/>
		</Box>
		</PageWrapper>
	);
};

export default ProgramDetails;
